import React, { useEffect, useRef, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * VAROTRADES — World-class Splash / Page-Transition Loader
 *
 * Design:
 *  - PURE white background
 *  - Real 3D supercar render (Nano Banana generated) blasting across the screen
 *  - Floating animated candlestick charts scattered everywhere (varying sizes)
 *  - VAROTRADES logo dramatic reveal: subtle scale + radial orange glow burst
 *  - Premium orange progress bar at the bottom
 */

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_SRC = `${API}/api/media/varotrade_official_emblem.png`;
const CAR_SRC = `${API}/api/media/generated/splash_supercar_cut.png?v=3`;

/* --------- Single candlestick SVG (memoized below) ---------- */
const Candle = ({ size = 56, up = true, opacity = 0.18, delay = 0 }) => {
  const colour = up ? '#10B981' : '#EF4444';
  return (
    <motion.svg
      width={size}
      height={size * 1.5}
      viewBox="0 0 40 60"
      initial={{ opacity: 0, y: 14, scale: 0.85 }}
      animate={{ opacity, y: 0, scale: 1 }}
      transition={{ duration: 0.9, delay, ease: 'easeOut' }}
      style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.04))' }}
    >
      <line x1="20" y1="2"  x2="20" y2="14" stroke={colour} strokeWidth="2" strokeLinecap="round" />
      <line x1="20" y1="46" x2="20" y2="58" stroke={colour} strokeWidth="2" strokeLinecap="round" />
      <rect x="6" y="14" width="28" height="32" rx="3" fill={colour} />
    </motion.svg>
  );
};

/* --------- Field of scattered candles ---------- */
const CandleField = () => {
  // Use deterministic positions so the field is consistent across renders.
  const candles = useMemo(() => ([
    { x: '4%',  y: '8%',  size: 36, up: true,  op: 0.10, d: 0.05 },
    { x: '12%', y: '22%', size: 78, up: false, op: 0.16, d: 0.12 },
    { x: '21%', y: '6%',  size: 48, up: true,  op: 0.12, d: 0.20 },
    { x: '30%', y: '30%', size: 32, up: true,  op: 0.10, d: 0.07 },
    { x: '6%',  y: '46%', size: 96, up: true,  op: 0.20, d: 0.18 },
    { x: '14%', y: '70%', size: 54, up: false, op: 0.14, d: 0.10 },
    { x: '4%',  y: '88%', size: 40, up: true,  op: 0.12, d: 0.22 },
    { x: '28%', y: '82%', size: 70, up: false, op: 0.18, d: 0.16 },
    { x: '40%', y: '12%', size: 44, up: false, op: 0.12, d: 0.04 },
    { x: '50%', y: '8%',  size: 30, up: true,  op: 0.08, d: 0.24 },
    { x: '62%', y: '20%', size: 60, up: true,  op: 0.16, d: 0.14 },
    { x: '74%', y: '4%',  size: 42, up: false, op: 0.10, d: 0.08 },
    { x: '88%', y: '14%', size: 56, up: true,  op: 0.14, d: 0.18 },
    { x: '94%', y: '36%', size: 80, up: true,  op: 0.18, d: 0.06 },
    { x: '60%', y: '78%', size: 36, up: false, op: 0.10, d: 0.20 },
    { x: '72%', y: '88%', size: 64, up: true,  op: 0.16, d: 0.12 },
    { x: '84%', y: '72%', size: 48, up: false, op: 0.14, d: 0.16 },
    { x: '92%', y: '90%', size: 38, up: true,  op: 0.10, d: 0.08 },
    { x: '46%', y: '90%', size: 30, up: false, op: 0.10, d: 0.22 },
    { x: '38%', y: '70%', size: 52, up: true,  op: 0.14, d: 0.10 },
  ]), []);

  return (
    <div className="absolute inset-0 pointer-events-none">
      {candles.map((c, i) => (
        <div key={i} className="absolute" style={{ left: c.x, top: c.y }}>
          <Candle size={c.size} up={c.up} opacity={c.op} delay={c.d} />
        </div>
      ))}
    </div>
  );
};

/* ---------------------------------------------------------------- */
/*  Splash component                                                 */
/* ---------------------------------------------------------------- */

const SplashLoader = ({ visible, variant = 'intro' }) => {
  const timing = variant === 'intro'
    ? { logoIn: 0.6, carDur: 2.6, exit: 0.5 }
    : { logoIn: 0.22, carDur: 0.95, exit: 0.28 };  // fast nav: ~1.2s total instead of ~2s

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="splash"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1, pointerEvents: 'auto' }}
          exit={{ opacity: 0, pointerEvents: 'none', transition: { duration: timing.exit, ease: 'easeOut' } }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-white overflow-hidden"
          aria-hidden="true"
          data-testid="splash-loader"
        >
          {/* === Candles field === */}
          <CandleField />

          {/* === Subtle orange radial glow center === */}
          <motion.div
            className="absolute pointer-events-none"
            style={{
              width: '60vw', height: '60vw',
              background: 'radial-gradient(circle, rgba(249,115,22,0.18) 0%, rgba(249,115,22,0.08) 28%, transparent 60%)',
              filter: 'blur(20px)',
            }}
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.1, ease: 'easeOut' }}
          />

          {/* === Speeding photorealistic supercar (BEHIND the logo) === */}
          <motion.img
            src={CAR_SRC}
            alt=""
            className="absolute pointer-events-none select-none"
            style={{
              top: '58%',
              left: 0,
              width: 'min(50vw, 660px)',
              minWidth: 320,
              zIndex: 1,
              filter: 'drop-shadow(0 24px 30px rgba(216,83,8,0.32)) saturate(1.08) contrast(1.04)',
            }}
            draggable={false}
            initial={{ x: '-55vw', opacity: 0 }}
            animate={{ x: '110vw', opacity: [0, 1, 1, 1, 0.9] }}
            transition={{ duration: timing.carDur, ease: [0.32, 0.72, 0.4, 1] }}
          />

          {/* === Speed lines behind the car === */}
          <motion.div
            className="absolute pointer-events-none"
            style={{ top: '62%', left: 0, right: 0, height: 4 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.6, 0.6, 0] }}
            transition={{ duration: timing.carDur, times: [0, 0.2, 0.7, 1] }}
          >
            <svg viewBox="0 0 1000 6" className="w-full h-full" preserveAspectRatio="none">
              {[0.3, 1.0, 1.6, 2.3, 3.0].map((y, i) => (
                <line
                  key={i}
                  x1="0" x2="1000" y1={y} y2={y}
                  stroke="url(#streakGrad)"
                  strokeWidth="0.6"
                  opacity={0.4 + i * 0.08}
                />
              ))}
              <defs>
                <linearGradient id="streakGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%"   stopColor="#F97316" stopOpacity="0" />
                  <stop offset="50%"  stopColor="#F97316" stopOpacity="0.8" />
                  <stop offset="100%" stopColor="#F97316" stopOpacity="0" />
                </linearGradient>
              </defs>
            </svg>
          </motion.div>

          {/* === Center stack: logo with radial glow + glow ring === */}
          <div className="relative flex flex-col items-center px-6" style={{ zIndex: 5 }}>
            {/* Glow ring behind logo */}
            <motion.div
              className="absolute rounded-full"
              style={{
                width: 240, height: 240,
                background: 'radial-gradient(circle, rgba(249,115,22,0.32) 0%, rgba(249,115,22,0.12) 35%, transparent 65%)',
              }}
              initial={{ scale: 0.2, opacity: 0 }}
              animate={{ scale: [0.2, 1.3, 1], opacity: [0, 1, 0.6] }}
              transition={{ duration: 1.4, ease: 'easeOut' }}
            />

            {/* Logo */}
            <motion.img
              src={LOGO_SRC}
              alt="VAROTRADES"
              className="relative h-20 md:h-24 lg:h-28 w-auto select-none"
              style={{ filter: 'drop-shadow(0 12px 28px rgba(216,83,8,0.22))' }}
              draggable={false}
              initial={{ y: -8, opacity: 0, scale: 0.86, filter: 'blur(8px)' }}
              animate={{ y: 0, opacity: 1, scale: 1, filter: 'blur(0px)' }}
              transition={{ duration: timing.logoIn, ease: [0.32, 1.4, 0.56, 1] }}
            />

            {/* Tagline */}
            <motion.div
              className="relative mt-3 text-[11px] md:text-xs font-black uppercase tracking-[0.42em] text-flame-700"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 0.85, y: 0 }}
              transition={{ duration: 0.6, delay: timing.logoIn + 0.15 }}
            >
              Trade · Smarter · Faster
            </motion.div>

            {/* Animated bottom progress bar */}
            <div
              className="relative mt-7 overflow-hidden rounded-full bg-flame-100/60"
              style={{ width: 'min(360px, 70vw)', height: 3 }}
            >
              <motion.div
                className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-flame-500 via-flame-600 to-orange-700"
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: timing.carDur * 0.75, delay: timing.logoIn * 0.3, ease: [0.65, 0, 0.35, 1] }}
              />
              <motion.div
                className="absolute inset-y-0 w-12 bg-white/40"
                initial={{ x: '-100%' }}
                animate={{ x: '420%' }}
                transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* --------------------------------------------------------------- */
/*  Controller: drives splash visibility on intro + route changes.  */
/* --------------------------------------------------------------- */

import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';

const SESSION_KEY = 'varotrades_intro_shown_v2';

export const SplashController = () => {
  const { pathname } = useLocation();
  const langCtx = useLanguage();
  const language = langCtx?.language;

  const [visible, setVisible] = useState(false);
  const [variant, setVariant] = useState('intro');
  const isInitialMount = useRef(true);
  const introActive = useRef(false);
  const hideTimer = useRef(null);

  // Intro on first load of the session
  useEffect(() => {
    let alreadyShown = false;
    try { alreadyShown = sessionStorage.getItem(SESSION_KEY) === '1'; } catch { /* ignore */ }
    if (!alreadyShown) {
      setVariant('intro');
      setVisible(true);
      introActive.current = true;
      hideTimer.current = setTimeout(() => {
        setVisible(false);
        introActive.current = false;
        try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* ignore */ }
      }, 2900);
      return () => { if (hideTimer.current) clearTimeout(hideTimer.current); };
    }
    return undefined;
  }, []);

  // Route-change & language-change loader
  useEffect(() => {
    if (isInitialMount.current) { isInitialMount.current = false; return undefined; }
    if (introActive.current) return undefined;
    if (hideTimer.current) clearTimeout(hideTimer.current);
    setVariant('route');
    setVisible(true);
    hideTimer.current = setTimeout(() => setVisible(false), 1200);
    return () => { if (hideTimer.current) clearTimeout(hideTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, language]);

  return <SplashLoader visible={visible} variant={variant} />;
};

export default SplashLoader;
